<template>
  <div class="mod-daily-login">
    <div class="page-header">
      <span class="page-header__title">每日登录统计</span>
      <span class="page-header__tip">活跃=当天有过登录态请求的去重人数，新用户=当天注册，回访占比=(活跃-新)/活跃</span>
    </div>

    <div class="page-body">
      <!-- 汇总卡片 -->
      <el-card shadow="never" class="summary-card" v-loading="summaryLoading">
        <div class="summary">
          <div v-for="item in summary" :key="item.label" class="summary__row">
            <div class="summary__label">{{ item.label }}</div>
            <div class="summary__cell">
              <div class="summary__cell-title">活跃用户数</div>
              <div class="summary__cell-value">{{ item.activeUsers }}</div>
              <div :class="rateClass(item.activeChangeRate)">{{ rateText(item.activeChangeRate) }}</div>
            </div>
            <div class="summary__cell">
              <div class="summary__cell-title">新用户数</div>
              <div class="summary__cell-value">{{ item.newUsers }}</div>
              <div :class="rateClass(item.newChangeRate)">{{ rateText(item.newChangeRate) }}</div>
            </div>
            <div class="summary__cell">
              <div class="summary__cell-title">回访占比</div>
              <div class="summary__cell-value">{{ percent(item.returnRatio) }}</div>
              <div :class="rateClass(item.returnRatioChangeRate)">{{ rateText(item.returnRatioChangeRate) }}</div>
            </div>
          </div>
        </div>
      </el-card>

      <!-- 走势图 -->
      <el-card shadow="never" class="trend-card">
        <div slot="header" class="trend-card__header">
          <span>每日登录用户走势（新注册 + 老用户登录，按设备类型）</span>
          <div>
            <el-date-picker
              v-model="trendRange"
              type="daterange"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              :clearable="false"
              class="trend-card__date">
            </el-date-picker>
            <el-button type="primary" icon="el-icon-search" @click="loadTrend">查询</el-button>
          </div>
        </div>
        <div v-loading="trendLoading" ref="trendChart" class="trend-card__chart"></div>
      </el-card>

      <!-- 某天登录会员 -->
      <el-card shadow="never" class="member-card">
        <div slot="header" class="member-card__header">
          <span>某天登录会员</span>
          <span class="member-card__tip">点击走势图上的某一天，可直接查看当天登录的会员</span>
        </div>
        <div class="toolbar">
          <el-date-picker
            v-model="dataForm.date"
            type="date"
            value-format="yyyy-MM-dd"
            placeholder="选择日期"
            :clearable="false"
            class="toolbar__item"
            @change="handleSearch">
          </el-date-picker>
          <el-select
            v-model="dataForm.deviceSource"
            placeholder="全部设备"
            clearable
            class="toolbar__item"
            @change="handleSearch">
            <el-option v-for="(label, value) in deviceSourceMap" :key="value" :label="label" :value="value"></el-option>
          </el-select>
          <el-select
            v-model="dataForm.isNewUser"
            placeholder="全部用户类型"
            clearable
            class="toolbar__item"
            @change="handleSearch">
            <el-option label="新注册" value="1"></el-option>
            <el-option label="老用户" value="0"></el-option>
          </el-select>
          <el-input
            v-model="dataForm.memberName"
            placeholder="账号"
            clearable
            class="toolbar__item"
            @keyup.enter.native="handleSearch"
            @clear="handleSearch">
          </el-input>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <div class="toolbar__right">
            <el-button icon="el-icon-download" @click="handleExport">导出 Excel</el-button>
          </div>
        </div>

        <el-table v-loading="listLoading" :data="dataList" border>
          <el-table-column label="会员ID" min-width="140">
            <template slot-scope="scope">{{ scope.row.memberCode }}</template>
          </el-table-column>
          <el-table-column label="账号" min-width="130">
            <template slot-scope="scope">{{ scope.row.memberName || '—' }}</template>
          </el-table-column>
          <el-table-column label="昵称" min-width="130">
            <template slot-scope="scope">{{ scope.row.memberNichen || '—' }}</template>
          </el-table-column>
          <el-table-column label="用户类型" width="100">
            <template slot-scope="scope">
              <el-tag :type="scope.row.isNewUser === 1 ? 'success' : 'info'" size="small">
                {{ scope.row.isNewUser === 1 ? '新注册' : '老用户' }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="设备来源" width="100">
            <template slot-scope="scope">{{ deviceSourceMap[scope.row.deviceSource] || scope.row.deviceSource || '—' }}</template>
          </el-table-column>
          <el-table-column label="上级代理" min-width="130">
            <template slot-scope="scope">{{ scope.row.agentAccount || '—' }}</template>
          </el-table-column>
          <el-table-column label="归属地" min-width="140">
            <template slot-scope="scope">{{ scope.row.address || '—' }}</template>
          </el-table-column>
          <el-table-column label="当天首次活跃" min-width="170">
            <template slot-scope="scope">{{ scope.row.dailyLoginTime || '—' }}</template>
          </el-table-column>
          <el-table-column label="注册时间" min-width="170">
            <template slot-scope="scope">{{ scope.row.createTime || '—' }}</template>
          </el-table-column>
        </el-table>

        <el-pagination
          class="pagination"
          :current-page="page"
          :page-size="limit"
          :total="total"
          :page-sizes="[10, 20, 50, 100]"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange">
        </el-pagination>
      </el-card>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import {
  getDailyLoginSummary,
  getDailyLoginTrend,
  getDailyLoginMembers,
  buildDailyLoginExportUrl,
  DEVICE_SOURCE_MAP
} from '@/api/dailyLogin'

// 统计口径全部截止到「昨日」：当天数据不完整，算进来会让环比失真
function yesterday () {
  const d = new Date()
  d.setDate(d.getDate() - 1)
  return d
}

function formatDate (date) {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

export default {
  name: 'DailyLogin',
  data () {
    const end = yesterday()
    const start = new Date(end)
    start.setDate(start.getDate() - 6)
    return {
      summary: [],
      summaryLoading: false,

      trendRange: [formatDate(start), formatDate(end)],
      trendLoading: false,
      trendChart: null,

      dataForm: {
        date: formatDate(end),
        deviceSource: '',
        isNewUser: '',
        memberName: ''
      },
      dataList: [],
      listLoading: false,
      page: 1,
      limit: 10,
      total: 0
    }
  },

  computed: {
    deviceSourceMap () {
      return DEVICE_SOURCE_MAP
    }
  },

  created () {
    this.loadSummary()
    this.loadList()
  },

  mounted () {
    this.loadTrend()
    window.addEventListener('resize', this.handleResize)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.handleResize)
    if (this.trendChart) {
      this.trendChart.dispose()
      this.trendChart = null
    }
  },

  methods: {
    loadSummary () {
      this.summaryLoading = true
      return getDailyLoginSummary().then(({ data: res }) => {
        if (res.code !== 0) {
          return this.$message.error(res.msg)
        }
        this.summary = res.data || []
      }).finally(() => {
        this.summaryLoading = false
      })
    },

    loadTrend () {
      this.trendLoading = true
      const [startDate, endDate] = this.trendRange || []
      return getDailyLoginTrend({ startDate, endDate }).then(({ data: res }) => {
        if (res.code !== 0) {
          return this.$message.error(res.msg)
        }
        this.renderTrend(res.data || [])
      }).finally(() => {
        this.trendLoading = false
      })
    },

    renderTrend (list) {
      if (!this.$refs.trendChart) {
        return
      }
      if (!this.trendChart) {
        this.trendChart = echarts.init(this.$refs.trendChart)
        // 点某一天 → 下方会员列表直接切到那天，省去再选一次日期
        this.trendChart.on('click', params => {
          if (params && params.name) {
            this.dataForm.date = params.name
            this.handleSearch()
          }
        })
      }
      const dates = list.map(item => item.date)
      const pick = key => list.map(item => item[key] || 0)
      this.trendChart.setOption({
        color: ['#3b6ef3', '#22c55e', '#f59e0b', '#ec4899', '#7c3aed', '#14b8a6'],
        tooltip: { trigger: 'axis' },
        legend: {
          data: ['总登录', '新注册', '老用户登录', 'H5', '安卓', 'iOS'],
          icon: 'circle',
          // echarts 6 的 legend 默认排到底部，会压住 x 轴，这里显式钉在顶部
          top: 0,
          left: 'center',
          textStyle: { color: '#606266' }
        },
        grid: { left: 10, right: 20, top: 50, bottom: 10, containLabel: true },
        xAxis: {
          type: 'category',
          data: dates,
          axisTick: { show: false },
          axisLine: { lineStyle: { color: '#e4e7ed' } },
          axisLabel: { color: '#909399' }
        },
        yAxis: {
          type: 'value',
          minInterval: 1,
          axisLabel: { color: '#909399' },
          splitLine: { lineStyle: { color: '#f0f2f5' } }
        },
        series: [
          { name: '总登录', type: 'line', smooth: true, data: pick('total') },
          { name: '新注册', type: 'line', smooth: true, data: pick('newCount') },
          { name: '老用户登录', type: 'line', smooth: true, data: pick('returningCount') },
          { name: 'H5', type: 'line', smooth: true, data: pick('h5Count') },
          { name: '安卓', type: 'line', smooth: true, data: pick('androidCount') },
          { name: 'iOS', type: 'line', smooth: true, data: pick('iosCount') }
        ]
      })
    },

    buildParams () {
      const params = {
        page: this.page,
        limit: this.limit,
        date: this.dataForm.date
      }
      if (this.dataForm.deviceSource) {
        params.deviceSource = this.dataForm.deviceSource
      }
      if (this.dataForm.isNewUser !== '') {
        params.isNewUser = this.dataForm.isNewUser
      }
      if (this.dataForm.memberName) {
        params.memberName = this.dataForm.memberName.trim()
      }
      return params
    },

    loadList () {
      if (!this.dataForm.date) {
        return
      }
      this.listLoading = true
      return getDailyLoginMembers(this.buildParams()).then(({ data: res }) => {
        if (res.code !== 0) {
          this.dataList = []
          this.total = 0
          return this.$message.error(res.msg)
        }
        this.dataList = (res.data && res.data.list) || []
        this.total = (res.data && res.data.total) || 0
      }).finally(() => {
        this.listLoading = false
      })
    },

    handleSearch () {
      this.page = 1
      this.loadList()
    },

    handleSizeChange (size) {
      this.limit = size
      this.page = 1
      this.loadList()
    },

    handleCurrentChange (page) {
      this.page = page
      this.loadList()
    },

    handleExport () {
      const params = this.buildParams()
      delete params.page
      delete params.limit
      window.location.href = buildDailyLoginExportUrl(params)
    },

    handleResize () {
      this.trendChart && this.trendChart.resize()
    },

    percent (value) {
      if (value === undefined || value === null) {
        return '0.0%'
      }
      return `${(value * 100).toFixed(1)}%`
    },

    rateText (value) {
      if (value === undefined || value === null || value === 0) {
        return '↑ 0.0%'
      }
      const arrow = value > 0 ? '↑' : '↓'
      return `${arrow} ${(Math.abs(value) * 100).toFixed(1)}%`
    },

    rateClass (value) {
      if (!value) {
        return 'summary__rate summary__rate--flat'
      }
      return value > 0 ? 'summary__rate summary__rate--up' : 'summary__rate summary__rate--down'
    }
  }
}
</script>

<style lang="scss" scoped>
.mod-daily-login {
  .page-header {
    display: flex;
    align-items: baseline;
    margin-bottom: 16px;

    &__title {
      font-size: 18px;
      font-weight: 600;
    }

    &__tip {
      margin-left: 12px;
      font-size: 12px;
      color: #909399;
    }
  }

  .summary-card,
  .trend-card {
    margin-bottom: 16px;
  }

  .summary {
    &__row {
      display: flex;
      align-items: stretch;
      border: 1px solid #ebeef5;
      border-bottom: none;

      &:last-child {
        border-bottom: 1px solid #ebeef5;
      }
    }

    &__label {
      width: 110px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 600;
      background: #fafafa;
      border-right: 1px solid #ebeef5;
    }

    &__cell {
      flex: 1;
      padding: 16px;
      text-align: center;
      border-right: 1px solid #ebeef5;

      &:last-child {
        border-right: none;
      }

      &-title {
        font-size: 13px;
        color: #909399;
      }

      &-value {
        margin: 6px 0;
        font-size: 26px;
        font-weight: 600;
        color: #303133;
      }
    }

    &__rate {
      font-size: 12px;

      &--up {
        color: #67c23a;
      }

      &--down {
        color: #f56c6c;
      }

      &--flat {
        color: #909399;
      }
    }
  }

  .trend-card {
    &__header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
    }

    &__date {
      margin-right: 12px;
    }

    &__chart {
      height: 360px;
    }
  }

  .member-card {
    &__header {
      display: flex;
      align-items: baseline;
    }

    &__tip {
      margin-left: 12px;
      font-size: 12px;
      color: #909399;
    }
  }

  .toolbar {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    margin-bottom: 16px;

    &__item {
      width: 170px;
      margin-right: 12px;
    }

    &__right {
      margin-left: auto;
    }
  }

  .pagination {
    margin-top: 16px;
    text-align: right;
  }
}
</style>
